import React, { useEffect, useState } from 'react';
import { CreditCard, ArrowRight, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api, formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import DiscordUsernameField from '@/components/DiscordUsernameField';
import { toast } from 'sonner';
import TermsCheckbox from '@/components/TermsCheckbox';

// PayPal wordmark (inline SVG — official brand colours, no external dependency)
const PayPalIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M20.067 8.478c.492.315.844.762 1.03 1.316.19.554.19 1.202.005 1.99-.24.998-.664 1.86-1.263 2.542-.593.68-1.31 1.184-2.15 1.507-.837.319-1.769.479-2.796.479h-.514c-.354 0-.66.256-.716.605l-.03.16-.593 3.762-.032.153a.72.72 0 0 1-.716.606H9.146c-.303 0-.518-.275-.463-.573l1.945-12.35a.86.86 0 0 1 .856-.72h5.008c1.06 0 2.02.216 2.878.647.86.435 1.552 1.083 2.077 1.944.526.86.789 1.977.789 3.352 0 .01.001.017.001.026-.061.144-.135.286-.221.428a4.32 4.32 0 0 1-1.949 1.626ZM17.31 5.9c-.526-.435-1.222-.653-2.087-.653h-3.83c-.354 0-.66.256-.716.606l-1.62 10.29h2.616l.593-3.762a.86.86 0 0 1 .855-.72h.514c1.028 0 1.96-.16 2.796-.478.84-.323 1.557-.827 2.15-1.507.6-.683 1.023-1.544 1.263-2.542.13-.542.174-.99.13-1.343-.045-.353-.196-.664-.451-.933-.256-.269-.628-.487-1.117-.653l-1.096-.305Z"/>
  </svg>
);

/**
 * PaymentMethodDialog
 * ─────────────────────────────────────────────────────────
 * Modal that lets the buyer pick Stripe (card + wallets) or
 * PayPal. Same endpoints & flow as before, just routed based
 * on the pick.
 */
export default function PaymentMethodDialog({
  open,
  onOpenChange,
  amount,
  kind,
  productId,
  vipTier,
  discountCode,
  platform,
  discordUsername,
}) {
  const [busy, setBusy] = useState(null); // "stripe" | "paypal" | null
  const [agreed, setAgreed] = useState(false);
  const { user } = useAuth();
  const askDiscord = discordUsername === undefined;
  const [dcName, setDcName] = useState('');
  useEffect(() => { if (user?.discord_username) setDcName((n) => n || user.discord_username); }, [user]);
  const dcClean = dcName.trim().replace(/^@/, '').toLowerCase();
  const dcValid = /^[a-z0-9_.]{2,32}$/.test(dcClean) && !dcClean.includes('..');
  const finalDiscord = askDiscord ? dcClean : discordUsername;
  const ready = agreed && (!askDiscord || dcValid);

  const start = async (label, endpoint) => {
    if (askDiscord && !dcValid) { toast.error('Enter your Discord username first'); return; }
    if (!agreed) { toast.error('Please tick the box to accept the Terms of Service'); return; }
    setBusy(label);
    try {
      const { data } = await api.post(endpoint, {
        kind,
        product_id: productId,
        vip_tier: vipTier || null,
        origin_url: window.location.origin,
        discount_code: discountCode || null,
        platform: platform || null,
        accepted_terms: agreed,
        discord_username: finalDiscord || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Checkout failed');
      setBusy(null);
    }
  };

  const startStripe = () => start('stripe', '/payments/checkout');
  const startPaypal = () => start('paypal', '/paypal/checkout');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="payment-method-dialog"
        className="bg-[#0d0d10] border border-white/10 text-white max-w-md rounded-2xl p-0 overflow-hidden"
      >
        <div className="orb pointer-events-none" style={{ top: '-30%', left: '50%', transform: 'translateX(-50%)', width: 400, height: 300, background: 'radial-gradient(circle, rgb(var(--brand-rgb)/0.28), transparent 70%)' }} />

        <DialogHeader className="px-7 pt-7 pb-3 relative">
          <p className="text-[10px] uppercase tracking-[0.4em] text-brand font-mono">Choose payment</p>
          <DialogTitle className="font-display font-black text-3xl mt-2 tracking-tighter">
            ${amount} <span className="text-white/50 text-lg font-normal">USD</span>
          </DialogTitle>
          {platform && <p data-testid="payment-platform-label" className="text-xs font-mono uppercase tracking-widest text-brand mt-1">Platform: {platform.toUpperCase()}</p>}
          <p className="text-sm text-white/50 mt-1">Pick how you&apos;d like to pay. Delivery is instant either way.</p>
        </DialogHeader>

        <div className="px-7 pb-7 space-y-3 relative">
          {askDiscord && <div className="-mt-6"><DiscordUsernameField value={dcName} onChange={setDcName} valid={dcValid} /></div>}
          <TermsCheckbox checked={agreed} onChange={setAgreed} />
          <button
            data-testid="pay-stripe-btn"
            onClick={startStripe}
            disabled={!!busy || !ready}
            className="w-full group flex items-center gap-4 p-4 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-brand/50 transition-all disabled:opacity-50 text-left"
          >
            <div className="h-11 w-11 rounded-lg bg-brand/15 border border-brand/30 flex items-center justify-center text-brand shrink-0">
              {busy === 'stripe' ? <Loader2 className="h-5 w-5 animate-spin" /> : <CreditCard className="h-5 w-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[15px]">Card, Apple Pay, Google Pay</div>
              <div className="text-xs text-white/50 mt-0.5">Powered by Stripe · Instant confirmation</div>
            </div>
            <ArrowRight className="h-4 w-4 text-white/40 group-hover:text-brand group-hover:translate-x-1 transition-all" />
          </button>

          <button
            data-testid="pay-paypal-btn"
            onClick={startPaypal}
            disabled={!!busy || !ready}
            className="w-full group flex items-center gap-4 p-4 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-[#0070ba]/60 transition-all disabled:opacity-50 text-left"
          >
            <div className="h-11 w-11 rounded-lg bg-[#0070ba]/15 border border-[#0070ba]/40 flex items-center justify-center text-[#009cde] shrink-0">
              {busy === 'paypal' ? <Loader2 className="h-5 w-5 animate-spin" /> : <PayPalIcon className="h-5 w-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[15px]">PayPal</div>
              <div className="text-xs text-white/50 mt-0.5">Log in to PayPal · Balance or linked card</div>
            </div>
            <ArrowRight className="h-4 w-4 text-white/40 group-hover:text-[#009cde] group-hover:translate-x-1 transition-all" />
          </button>

          <p className="text-[10px] uppercase tracking-widest text-white/30 font-mono text-center pt-3">
            Secure checkout · SSL encrypted
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
